import { Badge } from "@/components/ui/badge";
import { PRIVACY, QUOTE_STATUSES, DECISION, PROPOSAL_STATUS_CLS, proposalProgress, OPERATION_STATUS_CLS, operationProgress } from "@/lib/constants";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900" data-testid="page-title">
          {title}
        </h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function PrivacyBadge({ status, testid }) {
  const p = PRIVACY[status] || PRIVACY.GREEN;
  return (
    <span
      data-testid={testid}
      title={p.desc}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${p.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${p.dot}`} />
      {p.label}
    </span>
  );
}

export function QuoteStatusBadge({ status }) {
  const s = QUOTE_STATUSES[status] || QUOTE_STATUSES.Draft;
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${s.cls}`}>{s.label}</span>;
}

export function DecisionBadge({ decision, className = "" }) {
  const d = DECISION[decision] || DECISION.NEGOTIATE;
  return <span className={`inline-flex rounded-md border px-2.5 py-1 text-xs font-bold tracking-wide ${d.badge} ${className}`}>{d.label}</span>;
}

export function EmptyState({ children }) {
  return <div className="py-16 text-center text-sm text-muted-foreground">{children}</div>;
}

export function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

export function ProposalStatusBadge({ status }) {
  const cls = PROPOSAL_STATUS_CLS[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{status}</span>;
}

export function ProposalProgress({ status, showLabel = true }) {
  const { pct, kind } = proposalProgress(status);
  const barCls = kind === "negative" ? "bg-rose-500" : kind === "warning" ? "bg-amber-500" : kind === "done" ? "bg-emerald-500" : "bg-primary";
  return (
    <div className="w-full" data-testid="proposal-progress">
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barCls}`} style={{ width: `${pct}%` }} />
      </div>
      {showLabel && (
        <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
          <span>{status}</span><span>{pct}%</span>
        </div>
      )}
    </div>
  );
}

export function OperationStatusBadge({ status }) {
  const cls = OPERATION_STATUS_CLS[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{status}</span>;
}

export function OperationProgress({ status, type, showLabel = true }) {
  const { pct, kind } = operationProgress(status, type);
  const barCls = kind === "done" ? "bg-emerald-500" : "bg-primary";
  return (
    <div className="w-full" data-testid="operation-progress">
      <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barCls}`} style={{ width: `${pct}%` }} />
      </div>
      {showLabel && (
        <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
          <span>{status}</span><span>{pct}%</span>
        </div>
      )}
    </div>
  );
}
